import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import type { ClassSelect } from '@org/model';
import { useClassBlocks, StudentExerciseContent } from '../hooks/use-class-blocks';
import {
  useLegacyContentRenderer,
  ExerciseSubmission,
} from './use-content-renderer';
import { useExerciseManager } from '../hooks/use-exercise-manager';
import { ContentType } from '../../../types/contents';
import { toast } from 'sonner';
import { Link } from '@tanstack/react-router';
import { Button } from '../../core/button';
import { unwrapTrpcData } from '../../../utils';
import { ClassHeader } from './class-header';
import { ClassBlockList } from './class-block-list';
import { usePageContext } from '../../chat/chat-context';
import { ProtectedContentShell } from '../../security/protected-content-shell';
import { useAuthorization } from '../../login/use-authorization';
import type {
  ClassBlockSummary,
  LatestSubmissionResult,
  SubmitExerciseResult,
} from '../types/exercise';

interface ClassDetailPageProps {
  classId: string;
  blockIdx?: number;
  mode?: 'student' | 'teacher';
  viewerStudentId?: string;
  isReadOnly?: boolean;
  onJourneyAdvance?: (previousJourneyId: string) => void;
}

export function ClassDetailPageLegacy({
  classId,
  blockIdx,
  mode = 'student',
  viewerStudentId,
  isReadOnly = false,
  onJourneyAdvance,
}: ClassDetailPageProps) {
  const { t } = useTranslation('p2e');
  const [scrollProgress, setScrollProgress] = useState(0);
  const isTeacherView = mode === 'teacher';
  const normalizedBlockIdx = useMemo(() => {
    if (blockIdx === undefined || Number.isNaN(blockIdx)) {
      return undefined;
    }
    return blockIdx;
  }, [blockIdx]);

  // Fetch block summaries early so we can map sequential URL index to actual block order
  const {
    data: blockSummariesData,
    isLoading: isBlockSummariesLoading,
  } = trpc.blocks.getBlockSummariesByClass.useQuery(
    { classId },
    {
      enabled: Boolean(classId),
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const blockSummaries = useMemo<ClassBlockSummary[]>(
    () => unwrapTrpcData<ClassBlockSummary[]>(blockSummariesData) ?? [],
    [blockSummariesData]
  );

  const sortedBlockSummaries = useMemo(
    () => blockSummaries.slice().sort((a, b) => a.order - b.order),
    [blockSummaries]
  );

  // Map sequential URL index (0,1,2) to actual block order value for the API
  const blockOrderForIdx = useMemo(() => {
    if (normalizedBlockIdx === undefined || normalizedBlockIdx >= sortedBlockSummaries.length) {
      return undefined;
    }
    return sortedBlockSummaries[normalizedBlockIdx]?.order;
  }, [normalizedBlockIdx, sortedBlockSummaries]);

  const {
    blocks,
    isFiltered,
    isLoading: isBlocksLoading,
  } = useClassBlocks({ classId, blockIdx: blockOrderForIdx });

  // Record visit on mount
  const { mutate: recordVisit } = trpc.classes.recordClassVisit.useMutation();

  // Sections the teacher marked as homework for this reader's class session(s).
  const { data: homeworkBlocksData } = trpc.classes.homeworkBlocks.useQuery(
    { classId, studentId: viewerStudentId },
    { enabled: Boolean(classId), staleTime: 60_000 },
  );
  const homeworkBlockIds = homeworkBlocksData?.blockIds;
  const lastRecordedKeyRef = useRef<string | null>(null);
  
  useEffect(() => {
    if (!classId || !blocks.length || isReadOnly || isTeacherView) {
      return;
    }

    const sortedBlocksForVisit = blocks.slice().sort((a, b) => a.order - b.order);
    const activeBlock =
      normalizedBlockIdx !== undefined && sortedBlocksForVisit.length > 0
        ? sortedBlocksForVisit[normalizedBlockIdx] ?? sortedBlocksForVisit[0]
        : sortedBlocksForVisit[0];

    if (!activeBlock) {
      return;
    }

    const key = `${classId}-${activeBlock.id}`;
    if (lastRecordedKeyRef.current === key) {
      return;
    }

    recordVisit({
      classId,
      blockId: activeBlock.id,
    });
    lastRecordedKeyRef.current = key;
  }, [classId, normalizedBlockIdx, blocks, recordVisit, isReadOnly, isTeacherView]);

  // Resolve (journeyId, unitId) from the server — single source of truth,
  // derived from LearningProgress or UnitXClass∩UserXJourney. Avoids
  // brittle URL-query-param plumbing.
  const { data: classContext } = trpc.progress.resolveClassContext.useQuery(
    { classId },
    {
      enabled: Boolean(classId) && !isReadOnly && !isTeacherView,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );
  const journeyId = classContext?.journeyId;
  const unitId = classContext?.unitId;
  // Purchased/granted access whose window has closed: viewable, not completable.
  const journeyReadOnly = classContext?.journeyReadOnly ?? false;

  // Auto-complete class when student reaches the final block (D2)
  const utils = trpc.useUtils();
  const { mutate: completeClass } = trpc.progress.completeClass.useMutation({
    onSuccess: (result) => {
      utils.progress.getStudentJourneyWall.invalidate();
      utils.progress.getStudentJourneys.invalidate();
      if (result.journeyAdvance) {
        onJourneyAdvance?.(result.journeyAdvance.previousJourneyId);
      }
    },
  });
  const lastCompletedClassRef = useRef<string | null>(null);

  useEffect(() => {
    if (isReadOnly || isTeacherView || journeyReadOnly) return;
    if (!journeyId || !unitId || !classId) return;
    if (sortedBlockSummaries.length === 0) return;
    const isFinalBlock =
      normalizedBlockIdx !== undefined &&
      normalizedBlockIdx === sortedBlockSummaries.length - 1;
    if (!isFinalBlock) return;
    if (lastCompletedClassRef.current === classId) return;
    completeClass({ journeyId, unitId, classId });
    lastCompletedClassRef.current = classId;
  }, [classId, journeyId, unitId, normalizedBlockIdx, sortedBlockSummaries.length, isReadOnly, isTeacherView, journeyReadOnly, completeClass]);

  const exercisesRouter = trpc.exercises as any;
  // utils already declared above
  const exercisesUtils = utils.exercises as any;

  const { mutateAsync: submitExerciseMutation } = trpc.exercises.submit.useMutation();

  const {
    exerciseFeedback,
    handleSubmit: submitExerciseHandler,
    seedInitialFeedback,
    clearFeedback,
  } = useExerciseManager({
    submitExercise: submitExerciseMutation,
    exercisesUtils,
  });

  const { hasPermission } = useAuthorization();

  // Track scroll progress for image transition
  useEffect(() => {
    const handleScroll = () => {
      // Transition happens over the first 400px of scroll
      const scrollY = window.scrollY;
      const transitionDistance = 400;
      const progress = Math.min(scrollY / transitionDistance, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const {
    data: classData,
    isLoading: isClassLoading,
  } = trpc.classes.get.useQuery(
    { id: classId },
    {
      enabled: Boolean(classId),
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const exerciseIds = useMemo(() => {
    const ids = new Set<string>();
    blocks.forEach((block) => {
      block.contents.forEach((content) => {
        if (content.type === ContentType.Exercise && content.exerciseId) {
          ids.add(content.exerciseId);
        }
      });
    });
    return Array.from(ids);
  }, [blocks]);

  const cleanSubmissionsMutation = trpc.exercises.cleanClassSubmissions.useMutation({
    onSuccess: () => {
      toast.success('Submissions cleaned for this class.');
      clearFeedback();

      if (!exerciseIds.length) {
        return;
      }

      exercisesUtils.getLatestSubmissions.invalidate({
        exerciseIds,
        studentId: viewerStudentId,
        includeHistory: false,
      });

      if (isTeacherView && viewerStudentId) {
        exercisesUtils.getSubmissionHistory.invalidate({
          exerciseIds,
          studentId: viewerStudentId,
        });
      }
    },
    onError(error) {
      const message =
        error instanceof Error ? error.message : 'Failed to clean submissions.';
      toast.error(message);
    },
  });

  const handleCleanSubmissions = useCallback(() => {
    cleanSubmissionsMutation.mutateAsync({ classId }).then(() => {
      window.location.reload();
    });
  }, [classId, cleanSubmissionsMutation]);

  const {
    data: latestSubmissionsData,
    isLoading: isLatestSubmissionsLoading,
  } = exercisesRouter.getLatestSubmissions.useQuery(
    { exerciseIds, studentId: viewerStudentId, includeHistory: false },
    {
      enabled: exerciseIds.length > 0,
      staleTime: 1000 * 60 * 5,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const {
    data: submissionHistoryData,
    isLoading: isSubmissionHistoryLoading,
  } = exercisesRouter.getSubmissionHistory.useQuery(
    { exerciseIds, studentId: viewerStudentId },
    {
      enabled: exerciseIds.length > 0 && isTeacherView && Boolean(viewerStudentId),
      staleTime: 1000 * 60 * 5,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const latestSubmissionsByExerciseId = useMemo(() => {
    const submissions = (unwrapTrpcData(latestSubmissionsData) ?? []) as LatestSubmissionResult[];
    return submissions.reduce<Record<string, LatestSubmissionResult>>((acc, submission) => {
      acc[submission.exerciseId] = submission;
      return acc;
    }, {});
  }, [latestSubmissionsData]);

  const submissionHistoriesByExerciseId = useMemo(() => {
    if (!submissionHistoryData) {
      return {};
    }

    const historyEntries =
      unwrapTrpcData(submissionHistoryData) as { exerciseId: string; submissions: LatestSubmissionResult[] }[] | undefined;

    if (!historyEntries) {
      return {};
    }

    return historyEntries.reduce<Record<string, LatestSubmissionResult[]>>((acc, history) => {
      acc[history.exerciseId] = history.submissions ?? [];
      return acc;
    }, {});
  }, [submissionHistoryData]);

  useEffect(() => {
    seedInitialFeedback(latestSubmissionsByExerciseId);
  }, [latestSubmissionsByExerciseId, seedInitialFeedback]);

  usePageContext(useMemo(() => {
    if (!blocks || blocks.length === 0) return undefined;

    try {
      const contextPayload = blocks.map((block) => ({
        id: block.id,
        name: block.name,
        order: block.order,
        contents: block.contents.map((content) => {
          if (content.type === ContentType.Exercise && content.exerciseId) {
            return {
              ...content,
              latestSubmission: latestSubmissionsByExerciseId[content.exerciseId] ?? null,
            };
          }
          return content;
        }),
      }));

      return JSON.stringify(
        {
          classId,
          blocks: contextPayload,
        },
        null,
        2,
      );
    } catch (error) {
      console.error('Failed to serialize class detail context', error);
      return undefined;
    }
  }, [blocks, classId, latestSubmissionsByExerciseId]));

  // currentSummaryIndex uses sequential index directly (normalizedBlockIdx IS the sequential index)
  const currentSummaryIndex = normalizedBlockIdx !== undefined && normalizedBlockIdx < sortedBlockSummaries.length
    ? normalizedBlockIdx
    : -1;

  const previousIdx = currentSummaryIndex > 0 ? currentSummaryIndex - 1 : undefined;
  const nextIdx = currentSummaryIndex >= 0 && currentSummaryIndex < sortedBlockSummaries.length - 1
    ? currentSummaryIndex + 1 : undefined;

  const isFinalBlock =
    !isReadOnly &&
    !isTeacherView &&
    sortedBlockSummaries.length > 0 &&
    normalizedBlockIdx !== undefined &&
    normalizedBlockIdx === sortedBlockSummaries.length - 1;

  const { data: nextClassData, isLoading: isNextClassLoading } =
    trpc.progress.getNextClass.useQuery(
      { journeyId: journeyId!, unitId: unitId!, currentClassId: classId },
      {
        enabled: isFinalBlock && !!journeyId && !!unitId,
        staleTime: Infinity,
      }
    );

  const blockNavigation = useMemo(() => {
    if (isBlockSummariesLoading) {
      return (
        <div className="space-y-3 rounded-lg border border-border/60 bg-background/80 p-3">
          <div className="flex flex-wrap items-center gap-2 overflow-x-auto">
            {Array.from({ length: 4 }).map((_, index) => (
              <span key={`block-skeleton-${index}`} className="h-9 w-24 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        </div>
      );
    }

    if (sortedBlockSummaries.length === 0 || isTeacherView) {
      return null;
    }

    return (
      <div className="space-y-3 rounded-lg border border-border/60 bg-background/80 p-3" data-testid="block-nav">
        <div className="flex flex-wrap items-center gap-2 overflow-x-auto">
          {/* <Button
            asChild
            variant={normalizedBlockIdx === undefined ? 'secondary' : 'outline'}
            size="sm"
          >
            <Link to={`/classes/${classId}/`}>
              Overview
            </Link>
          </Button> */}
          {sortedBlockSummaries.map((summary, idx) => {
            const isActive = idx === normalizedBlockIdx;
            const label = summary.name?.trim() || `Block ${idx + 1}`;
            return (
              <Button
                key={summary.blockId}
                asChild
                variant={isActive ? 'secondary' : 'outline'}
                size="sm"
                className="min-h-[44px] md:min-h-0"
              >
                <Link to={`/classes/${classId}/block/${idx}`}>
                  {label}
                </Link>
              </Button>
            );
          })}
        </div>
      </div>
    );
  }, [classId, isBlockSummariesLoading, normalizedBlockIdx, sortedBlockSummaries]);

  const canCleanSubmissions = hasPermission('submissions:delete');

  const headerActions =
    blockNavigation || canCleanSubmissions || journeyReadOnly ? (
      <div className="space-y-3">
        {journeyReadOnly && (
          <p
            data-testid="journey-read-only-notice"
            className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
          >
            {t('class.journeyReadOnly')}
          </p>
        )}
        {blockNavigation}
        {canCleanSubmissions && (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              data-testid="restart-class-btn"
              title="Clear your answers for this class"
              isLoading={cleanSubmissionsMutation.isPending}
              disabled={!exerciseIds.length}
              onClick={handleCleanSubmissions}
            >
              {t('class.restartClass')}
            </Button>
          </div>
        )}
      </div>
    ) : null;

  const handleExerciseSubmit = useCallback(
    async (content: StudentExerciseContent, submission: ExerciseSubmission) => {
      if (!content.exerciseId) {
        toast.error('This exercise is missing required metadata. Please contact your instructor.');
        return;
      }

      await submitExerciseHandler(content, submission);
    },
    [submitExerciseHandler],
  );

  const { renderContent } = useLegacyContentRenderer({
    onExerciseSubmit: isReadOnly ? undefined : handleExerciseSubmit,
    exerciseFeedback,
    isReadOnly,
    viewerStudentId,
  });

  const isLoading =
    isClassLoading || isBlockSummariesLoading || isBlocksLoading || isLatestSubmissionsLoading || (isTeacherView && isSubmissionHistoryLoading);
  const sortedBlocks = useMemo(
    () => blocks.slice().sort((a, b) => a.order - b.order),
    [blocks]
  );
  const isMissingBlock =
    !isLoading && isFiltered && sortedBlocks.length === 0;
  const requestedBlockLabel = normalizedBlockIdx !== undefined ? normalizedBlockIdx + 1 : undefined;

  const classInfo = classData as unknown as ClassSelect | null;

  if (isLoading) {
    return (
      <div className="container mx-auto space-y-6 px-4 py-6 md:p-8">
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="h-64 animate-pulse rounded-lg bg-muted" />
        <div className="space-y-4">
          <div className="h-6 w-64 animate-pulse rounded bg-muted" />
          <div className="h-24 animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }

  if (!classInfo) {
    return (
      <div className="container mx-auto px-4 py-6 md:p-8">
        <p className="text-muted-foreground">Class not found.</p>
      </div>
    );
  }

  const warningMessage =
    'Printing, exporting, or redistributing this English2Empower class content is prohibited.';
  const watermarkText =
    mode === 'teacher' ? 'English2Empower • Teacher Preview' : 'English2Empower • Student Session';

  return (
    <ProtectedContentShell
      contentClassName="relative min-h-screen"
      warningMessage={warningMessage}
      disableWatermark={true}
      // watermarkText={water markText}
    >
      {/* Background Image - fades in as you scroll */}
      {classInfo?.imageUrl && scrollProgress > 0 && (
        <div
          className="fixed inset-x-0 top-0 z-0 transition-opacity duration-300"
          style={{
            height: '50vh',
            opacity: Math.min(scrollProgress * 1.2, 0.6),
          }}
        >
          <img
            src={classInfo.imageUrl}
            alt=""
            className="h-full w-full object-cover"
            style={{
              filter: `blur(${Math.min(scrollProgress * 4, 3)}px)`,
            }}
          />
          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/20 to-background/80" />
        </div>
      )}

      <div className="container relative z-10 mx-auto space-y-10 px-4 py-6 md:px-8 md:py-10">
        <ClassHeader classInfo={classInfo} scrollProgress={scrollProgress}>
          {headerActions}
        </ClassHeader>

        <section className="relative space-y-8">
          <ClassBlockList
            blocks={blocks}
            latestSubmissionsByExerciseId={latestSubmissionsByExerciseId}
            submissionHistoriesByExerciseId={submissionHistoriesByExerciseId}
            renderContent={renderContent}
            isFiltered={isFiltered}
            isMissingBlock={isMissingBlock}
            homeworkBlockIds={homeworkBlockIds}
            requestedBlockLabel={requestedBlockLabel}
            scrollProgress={scrollProgress}
            mode={mode}
            navigation={
              normalizedBlockIdx !== undefined
                ? {
                    previousIdx,
                    nextIdx,
                    normalizedBlockIdx,
                    classId,
                    finalAction:
                      isFinalBlock && journeyId && unitId
                        ? isNextClassLoading
                          ? { label: t('class.nextClass'), isLoading: true }
                          : nextClassData?.isLastInUnit
                            ? { to: '/journey', label: t('class.backToJourney') }
                            : nextClassData?.nextClassId
                              ? {
                                  to: `/classes/${nextClassData.nextClassId}/block/0?journeyId=${journeyId}&unitId=${unitId}`,
                                  label: t('class.nextClass'),
                                }
                              : undefined
                        : undefined,
                  }
                : undefined
            }
          />
        </section>
      </div>
    </ProtectedContentShell>
  );
}
